'use client'

import { LngLat, LngLatBounds } from 'maplibre-gl'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useAccount } from 'wagmi'
import { useQueryClient } from '@tanstack/react-query'
import SlapMap from './map/SlapMap'
import {
  Board,
  emptyBoard,
  Place,
  PlaceId,
  SidebarMode,
  DisplayedCenter,
  defaultDisplayedCenter,
  Slap,
  SlapId,
  SelectedItem,
  emptyBounds,
  StickerDesign,
  StickerDesignId,
  PendingSlapList,
  ExpandedSlap,
  convertExpandedSlapToSlap,
  FocusedPlace,
  Activity,
  ActivityList,
  PublishActivity,
  SlapActivity
} from './utils/Primitives'
import {
  useEnhancedSlapActivity,
  EnhancedActivity
} from './utils/useEnhancedSlapActivity'
import { MultiSlapResourceTracker } from './slaps/SlapResourceTracker'
import { MultiStickerResourceTracker } from './stickers/StickerResourceTracker'
import SlapExecutooor from './slaps/SlapExecutooor'
import SlapCheckWarnings, { SlapWarning } from './slaps/SlapCheckWarnings'
import DeleteSlapDialog from './slaps/DeleteSlapDialog';
import {
  lookupSlap,
  writeSlapToBoard,
  deleteSlapFromBoard,
  setStoredSlapSubmitted,
  getOrLoadSlap
} from './slaps/Slaps'
import { 
  writeStickerDesignToBoard, 
  getStickerDesign,
  getOrLoadStickerDesign
} from './stickers/StickerDesigns'
import StickerCreator from './stickers/StickerCreator'
import {
  getCurrentTimestamp,
  formatEthAddress
} from './utils/GeneralUtilities'
import { useRealtime } from './utils/Realtime'
import { getObjectives, initObjectiveStore } from './objectives/Objectives'
import { useFetchSavePaymentMethods } from './payments/PaymentMethods'
import AddNewToken from './payments/AddNewToken'
import PayoutReviewer from './payments/PayoutReviewer'
import Link from 'next/link'
import GameTitle from './components/GameTitle';
import GameTitleMobile from './components/GameTitleMobile';
import GameSidebar from './sidebar/GameSidebar';
import StickerBrowser from './stickers/StickerBrowser'
import StickerReviewer from './stickers/StickerReviewer'
import SlapOrientationModal from './components/SlapOrientationModal';
import SlapReviewer from './slaps/SlapReviewer';
import PlayerReviewer from './player/PlayerReviewer';
import { getPlace } from './places/Places';
import { fetchPlayer } from './player/Players';
import { Address, getAddress } from 'viem';
import LocationBadge from './components/LocationButton';
import { BlockPlaces } from '@ivyroot/block-places';
import { PublishedStickerData } from './utils/Primitives';
import RecentActivity from './activity/RecentActivity';
import SlapperButtonSection from './slaps/SlapperButtonSection';
import ConnectAccount from './components/ConnectAccount';

export const SlapMode = 'slap'
export const CreateMode = 'create'
export const AddTokenMode = 'add-token'
export const StickerBrowserMode = 'sticker-browser'

const StickercloudWebsocketUrl =
  process.env.NEXT_PUBLIC_STICKERCLOUD_WEBSOCKET_API as string
if (!StickercloudWebsocketUrl || StickercloudWebsocketUrl.length === 0) {
  console.error(`WEBSOCKET API URL is not set`)
}

type MapViewTarget = {
  center: [number, number];
  zoom?: number;
  bounds?: LngLatBounds | null;
};

export function Game(props: { initialPlaceId?: PlaceId }) {
  const { initialPlaceId } = props
  const { address: playerAddress } = useAccount()
  const queryClient = useQueryClient()
  const defaultLngLat = new LngLat(0, 0)
  const startingBoard = emptyBoard
  const mainBoardRef: React.MutableRefObject<Board> = useRef(startingBoard)
  const gameDescription =
    'Pick a sticker and use the map to slap it anywhere in the world:'
  const [player, setPlayer] = useState<any>(null)
  const [activities, setActivities] = useState<EnhancedActivity[]>([])
  const [focusedPlace, setFocusedPlace] = useState<FocusedPlace>({
    placeId: initialPlaceId ?? null,
  });
  const [draftSlaps, setDraftSlaps] = useState<PendingSlapList | null>(null)
  const [deletedSlaps, setDeletedSlaps] = useState<SlapId[]>([])
  const [currentSlapSize, setCurrentSlapSize] = useState<number>(1)
  const [currentStickerId, setCurrentStickerId] = useState<StickerDesignId | null>(null);
  const [previewStickerId, setPreviewStickerId] = useState<StickerDesignId | null>(null)
  const skipStickerReviewRef = useRef<Set<StickerDesignId>>(new Set());
  const skipSlapReviewRef = useRef<Set<SlapId>>(new Set());
  const [previewSlapId, setPreviewSlapId] = useState<SlapId | null>(null)
  const [previewPlayerAddress, setPreviewPlayerAddress] = useState<Address | null>(null)
  const [showOrientationModal, setShowOrientationModal] = 
    useState<boolean>(false)
  const [orientationStickerName, setOrientationStickerName] = 
    useState<string | undefined>(undefined)
  const [latestSlap, setLatestSlap] = useState<Slap | null>(null)
  const logoSrc = '/static/logo-main.png'
  const [slapExecutorOpenTimestamp, setSlapExecutorOpenTimestamp] = useState<number>(0)
  const [deleteSlapDialogOpenTimestamp, setDeleteSlapOpenTimestamp] =
    useState<number>(0)
  const [deleteSlapId, setDeleteSlapId] = useState<SlapId | null>(null)
  const nextDraftSlapid = useRef<number>(100000000)
  const [currMode, setCurrMode] = useState<string>(SlapMode)
  const [currDisplayedCenter, setCurrDisplayedCenter] =
    useState<DisplayedCenter>(defaultDisplayedCenter)
  const [latestUpdateTime, setLatestUpdateTime] = useState<number | null>(null);
  const hasPendingSlaps = !!draftSlaps && draftSlaps.length > 0
  const [lockedMapView, setLockedMapView] = useState<MapViewTarget | null>(null)
  const [mapLocked, setMapLocked] = useState<boolean>(false)
  const lastDraftIdRef = useRef<number | null>(null)
  const draftCenterRef = useRef<[number, number] | null>(null)
  const [currentSticker, setCurrentSticker] = useState<StickerDesign | null>(null)
  const [activeSidebarMode, setActiveSidebarMode] =
    useState<SidebarMode | null>(SidebarMode.Latest);
  const [focusedObjectiveId, setFocusedObjectiveId] = useState<number | null>(null);
  const [payoutReviewerOpen, setPayoutReviewerOpen] = useState<boolean>(false);
  const [isSlapDialogOpen, setIsSlapDialogOpen] = useState<boolean>(false);
  const [isShareOverlayOpen, setIsShareOverlayOpen] = useState<boolean>(false);
  const [slapPreCheckWarnings, setSlapPreCheckWarnings] = useState<SlapWarning[]>([]);
  const [recentlyPublishedStickers, setRecentlyPublishedStickers] =
    useState<PublishedStickerData[]>([]);
  const [indexingSlaps, setIndexingSlaps] = useState<Set<SlapId>>(new Set());
  const [indexingStickers, setIndexingStickers] =
    useState<Set<StickerDesignId>>(new Set());
  const [resourceTrackedStickers, setResourceTrackedStickers] = 
    useState<Set<StickerDesignId>>(new Set());
  const [mapReady, setMapReady] = useState(false)
  const [pendingFlushSlapId, setPendingFlushSlapId] = useState<SlapId | null>(
    null
  )
  const [pendingUrlPlaceId, setPendingUrlPlaceId] = useState<PlaceId | null>(null)
  const pendingFlushSlapIdRef = useRef<SlapId | null>(null)
  const stickerBrowserScrollRef = useRef<number>(0)

  const shouldHideActivity =
    currMode !== SlapMode ||
    previewStickerId !== null ||
    previewSlapId !== null ||
    previewPlayerAddress !== null ||
    showOrientationModal ||
    payoutReviewerOpen ||
    isSlapDialogOpen ||
    isShareOverlayOpen;

  const closeStickerReviewer = useCallback(() => {
    setPreviewStickerId(null);
  }, []);

  const closeSlapReviewer = useCallback(() => {
    setPreviewSlapId(null);
  }, []);

  const closePlayerReviewer = useCallback(() => {
    setPreviewPlayerAddress(null);
  }, []);

  const clearReviews = useCallback(() => {
    closeStickerReviewer();
    closeSlapReviewer();
    closePlayerReviewer();
  }, [closeStickerReviewer, closeSlapReviewer, closePlayerReviewer]);

  // Helper to clear all client-side draft slaps (after server confirms)
  const clearAllDraftSlaps = useCallback(() => {
    if (draftSlaps && draftSlaps.length > 0) {
      const idsToDelete: SlapId[] = []
      draftSlaps.forEach(expanded => {
        const s: Slap = convertExpandedSlapToSlap(expanded)
        deleteSlapFromBoard({ boardRef: mainBoardRef, slap: s })
        idsToDelete.push(s.id)
      })
      setDraftSlaps([])
      setDeletedSlaps(idsToDelete)
      setLatestUpdateTime(Date.now())
    }
  }, [draftSlaps, mainBoardRef])

  const handleReviewSlapId = useCallback((slapId: SlapId) => {
    clearReviews();
    console.log(`reviewing slap ${slapId}`)
    
    // First check if slap is already in board (should be the case after sync)
    const existingSlap = lookupSlap({ boardRef: mainBoardRef, slapId });
    if (existingSlap) {
      console.log(`Slap ${slapId} found in board, opening modal immediately`);
      setPreviewSlapId(slapId);
      return;
    }

    // Only fall back to API if not found locally
    console.log(`Slap ${slapId} not found in board, fetching from API`);
    // Add to indexing set
    setIndexingSlaps(prev => new Set([...Array.from(prev), slapId]));

    // Try to load the slap from API
    getOrLoadSlap({ boardRef: mainBoardRef, slapId })
      .then(slap => {
        if (slap) {
          setPreviewSlapId(slapId);
        }
      })
      .catch(error => {
        console.error('Error loading slap:', error);
      })
      .finally(() => {
        // Remove from indexing set regardless of success/failure
        setIndexingSlaps(prev => {
          const next = new Set(Array.from(prev));
          next.delete(slapId);
          return next;
        });
      });
  }, [clearReviews, mainBoardRef])

  // Handle when a slap resource sync completes successfully
  const handleSlapSyncComplete = useCallback(
    (slapId: SlapId, placeId: PlaceId) => {
      console.log(
        `Slap sync completed for slap ${slapId} at place ${placeId}`
      )

      // Invalidate queries to refresh data (matches all latestSlaps queries)
      queryClient.invalidateQueries({ queryKey: ['latestSlaps'], exact: false })
      // Also invalidate any place-specific queries
      queryClient.invalidateQueries({ queryKey: ['place', placeId] })

      // If slap is already in store, finalize immediately
      const found = lookupSlap({ boardRef: mainBoardRef, slapId })
      if (found) {
        setLatestSlap(found)
        clearAllDraftSlaps()
        // Open the new slap in modal - BOOM!
        if (!skipSlapReviewRef.current.has(slapId)) {
          handleReviewSlapId(slapId)
        } else {
          skipSlapReviewRef.current.delete(slapId)
        }
        return
      }

      // Otherwise, set pending flush and schedule a single delayed fetch
      setPendingFlushSlapId(slapId)
      setTimeout(() => {
        if (pendingFlushSlapIdRef.current !== slapId) {
          return
        }
        const again = lookupSlap({ boardRef: mainBoardRef, slapId })
        if (again) {
          setLatestSlap(again)
          clearAllDraftSlaps()
          setPendingFlushSlapId(null)
          // Open the new slap in modal - BOOM!
          if (!skipSlapReviewRef.current.has(slapId)) {
            handleReviewSlapId(slapId)
          } else {
            skipSlapReviewRef.current.delete(slapId)
          }
          return
        }
        getOrLoadSlap({ boardRef: mainBoardRef, slapId }).then(loaded => {
          if (pendingFlushSlapIdRef.current !== slapId) {
            return
          }
          if (loaded) {
            setLatestSlap(loaded)
            clearAllDraftSlaps()
            setPendingFlushSlapId(null)
            // Open the new slap in modal - BOOM!
            if (!skipSlapReviewRef.current.has(slapId)) {
              handleReviewSlapId(slapId)
            } else {
              skipSlapReviewRef.current.delete(slapId)
            }
          } else {
            console.log(
              `Could not load slap ${slapId} after delay; keeping drafts`
            )
          }
        })
      }, 3000)
    },
    [queryClient, clearAllDraftSlaps, handleReviewSlapId]
  )

  // Keep the ref synced with state for timeout checks
  useEffect(() => {
    pendingFlushSlapIdRef.current = pendingFlushSlapId
  }, [pendingFlushSlapId])

  // Finalize flush when slap appears in store via websocket or other writes
  useEffect(() => {
    if (!pendingFlushSlapId) {
      return
    }
    const found = lookupSlap({
      boardRef: mainBoardRef,
      slapId: pendingFlushSlapId,
    })
    if (found) {
      setLatestSlap(found)
      clearAllDraftSlaps()
      setPendingFlushSlapId(null)
      // Open the new slap in modal - BOOM!
      handleReviewSlapId(pendingFlushSlapId)
    }
  }, [pendingFlushSlapId, latestUpdateTime, handleReviewSlapId, clearAllDraftSlaps])

  // Enhanced slap activity management with resource locator integration
  const {
    addEnhancedSlapActivity,
    handleResourceComplete,
    handleResourceError,
    getSlapIndexingStatus,
    resourceTrackedSlaps
  } = useEnhancedSlapActivity(
    mainBoardRef,
    activities,
    setActivities,
    indexingSlaps,
    setIndexingSlaps,
    playerAddress,
    handleSlapSyncComplete
  );

  const addPublishActivity = useCallback((
    stickerId: StickerDesignId,
    stickerName: string,
    imageCID: string,
    metadataCID?: string
  ) => {
    const newActivity: PublishActivity = {
      id: `publish-${Date.now()}`,
      timestamp: Date.now(),
      type: 'publish',
      stickerId: Number(stickerId),
      stickerName,
      imageCID,
      ...(metadataCID ? { metadataCID } : {})
    }
    setActivities(prev => {
      const existingIndex = prev.findIndex(activity => {
        const isPublish = activity.type === 'publish'
        const sameId =
          Number((activity as PublishActivity).stickerId) ===
          Number(stickerId)
        return isPublish && sameId
      })
      if (existingIndex !== -1) {
        const updated = [...prev]
        updated[existingIndex] = newActivity
        return updated
      }
      return [newActivity, ...prev].slice(0, 10)
    })

    // Auto-dismiss activity after 30 seconds
    setTimeout(() => {
      setActivities(prev => prev.filter(activity => {
        const isPublish = activity.type === 'publish'
        const sameId =
          Number((activity as PublishActivity).stickerId) ===
          Number(stickerId)
        return !(isPublish && sameId)
      }));
    }, 30000);
  }, [])

  const addSlapActivity = useCallback((
    slapId: SlapId,
    placeId: PlaceId,
    stickerId: StickerDesignId,
    size: number,
    isUserCreated: boolean = false
  ) => {
    console.log(
      'Adding slap activity:',
      slapId,
      ', user-created:',
      isUserCreated
    );
    if (isUserCreated) {
      skipSlapReviewRef.current.add(slapId);
    }
    addEnhancedSlapActivity(slapId, placeId, stickerId, size, isUserCreated);
  }, [addEnhancedSlapActivity])

  // Fetch player data when address changes
  useEffect(() => {
    if (playerAddress && mainBoardRef) {
      const playerData = fetchPlayer({ boardRef: mainBoardRef, address: playerAddress })
      setPlayer(playerData)
    } else {
      setPlayer(null)
    }
  }, [playerAddress, mainBoardRef])

  useEffect(() => {
    // Access localStorage only on the client side
    const storedStickerId = localStorage.getItem('currentStickerId');
    if (storedStickerId) {
      setCurrentStickerId(parseInt(storedStickerId, 10));
    }
  }, []);

  const updateCurrentStickerId = (newStickerId: StickerDesignId | null) => {
    setCurrentStickerId(newStickerId);
    if (newStickerId !== null) {
      localStorage.setItem('currentStickerId', newStickerId.toString());
    }
  };

  useEffect(() => {
    if (!currentStickerId) {
      setCurrentSticker(null);
      return;
    }
    const existing = getStickerDesign({ boardRef: mainBoardRef, stickerId: currentStickerId });
    if (existing) {
      setCurrentSticker(existing);
      return;
    }
    setCurrentSticker(null);
    void getOrLoadStickerDesign({ boardRef: mainBoardRef, stickerId: currentStickerId })
      .then((loaded) => {
        if (loaded && loaded.id === currentStickerId) {
          setCurrentSticker(loaded);
        }
      });
  }, [currentStickerId, mainBoardRef]);

  // Add payment methods initialization
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
  } = useFetchSavePaymentMethods({
    boardRef: mainBoardRef
  });

  // Initialize objectives and payment methods on component mount
  useEffect(() => {
    initObjectiveStore(mainBoardRef)
  }, []) // Empty dependency array means this runs once on mount

  // websocket-based realtime feed
  const {
    slapHistory: realtimeSlapHistory,
    latestSlap: realtimeLatestSlap,
    latestStickerDesign: realtimeLatestStickerDesign,
  } = useRealtime({
    url: StickercloudWebsocketUrl,
  });

  // when new sticker designs arrive write them to the board
  useEffect(() => {
    if (realtimeLatestStickerDesign) {
      writeStickerDesignToBoard({
        boardRef: mainBoardRef,
        sticker: realtimeLatestStickerDesign
      })
      // Add activity for published sticker
      addPublishActivity(
        realtimeLatestStickerDesign.id,
        realtimeLatestStickerDesign.name,
        realtimeLatestStickerDesign.imageCID || '',
        realtimeLatestStickerDesign.metaCID || undefined
      );
    }
  }, [realtimeLatestStickerDesign, addPublishActivity]);

  const clearFocusedPlace = useCallback(() => {
    setFocusedPlace({ placeId: null });
  }, []);

  const deleteDraftSlap = useCallback((slapId: SlapId) => {
    const slap = lookupSlap({ boardRef: mainBoardRef, slapId })
    if (!slap) {
      console.log(`cannot find slap to delete ${slapId}`)
      return
    }
    deleteSlapFromBoard({ boardRef: mainBoardRef, slap })
    if (latestSlap && latestSlap.id == slap.id) {
      setLatestSlap(null)
    }
    setDraftSlaps(prevState => {
      if (prevState) {
        return prevState.filter(fullSlap => fullSlap.id !== slap.id)
      }
      return []
    })
    setDeletedSlaps([slapId])
    setLatestUpdateTime(Date.now())
  }, [mainBoardRef, latestSlap]);

  // when live slaps by the player come in remove draft slaps
  useEffect(() => {
    if (realtimeLatestSlap && playerAddress) {
      // Add to indexing set immediately when we get a new slap
      setIndexingSlaps(prev =>
        new Set([...Array.from(prev), realtimeLatestSlap.id])
      );

      if (realtimeLatestSlap.player.toLowerCase() === playerAddress.toLowerCase()) {
        draftSlaps?.forEach(draftSlap => {
          if (draftSlap.place.id == realtimeLatestSlap.place &&
            draftSlap.sticker.id == realtimeLatestSlap.sticker &&
            draftSlap.player.toLowerCase() == playerAddress.toLowerCase()) {
            console.log(`deleting draft slap ${draftSlap.id}`)
            const sl: Slap = convertExpandedSlapToSlap(draftSlap)
            setDraftSlaps(prevState => {
              if (prevState) {
                return prevState.filter(fullSlap => fullSlap.id !== draftSlap.id)
              }
              return []
            })
          }
        });
      }
      // Add activity for published slap
      addSlapActivity(
        realtimeLatestSlap.id,
        realtimeLatestSlap.place,
        realtimeLatestSlap.sticker,
        realtimeLatestSlap.size
      );

      // Remove from indexing set after a short delay to ensure the slap is loaded
      setTimeout(() => {
        setIndexingSlaps(prev => {
          const next = new Set(Array.from(prev));
          next.delete(realtimeLatestSlap.id);
          return next;
        });
      }, 2000); // 2 second delay to ensure loading is complete
    }
  }, [realtimeLatestSlap, playerAddress, draftSlaps, deleteDraftSlap, addSlapActivity]);


  const startSlapping = useCallback(() => {
    setCurrMode(SlapMode)
    if (currMode === StickerBrowserMode && typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      })
    }
  }, [currMode])

  const startCreating = () => {
    setCurrMode(CreateMode)
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      })
    }
  }

  const startAddingToken = () => {
    setCurrMode(AddTokenMode)
  }

  const handleSlapExecutorOpen = () => {
    setSlapExecutorOpenTimestamp(new Date().getTime())
  }

  const handleSlapSizeWasChanged = (newSize: number) => {
    setCurrentSlapSize(newSize)
  }

  const setFocusOnPlace = (focusedPlace: FocusedPlace) => {
    setFocusedPlace(focusedPlace);
    if (focusedPlace.placeId) {
      setActiveSidebarMode(SidebarMode.Place);
    }
  }

  const handleMoveMapToPlace = (placeId: PlaceId) => {
    const place = getPlace({ boardRef: mainBoardRef, placeId })
    if (place) {
      setFocusedPlace({ placeId: placeId });
    }
  }

  const handleStartPublishing = () => {
    startCreating();
  }

  const handleReviewStickerId = useCallback((stickerId: StickerDesignId): void => {
    clearReviews();
    setPreviewStickerId(stickerId);
  }, [clearReviews]);

  const handleStickerChoice = (sticker: StickerDesign) => {
    updateCurrentStickerId(sticker.id);
    startSlapping();
    closeStickerReviewer();
  };


  const handleReviewPlayer = (playerAddress: Address) => {
    clearReviews();
    console.log(`reviewing player ${playerAddress}`)
    const normalizedAddress = getAddress(playerAddress);
    setPreviewPlayerAddress(normalizedAddress);
  }

  const handleReviewSticker = (stickerDesignId: StickerDesignId) => {
    setPreviewStickerId(stickerDesignId);
  }

  const getNewDraftSlapid = (): number => {
    const newId = nextDraftSlapid.current
    nextDraftSlapid.current += 1
    return newId
  }

  const createDraftSlap = async (
    place: Place,
    size: number,
    sticker: StickerDesign
  ) => {
    if (draftSlaps && draftSlaps.length > 0) {
      return
    }
    const currTs = getCurrentTimestamp()
    const slapUseId = getNewDraftSlapid()
    const definitelyAnAddress = playerAddress ? playerAddress : '0x2032032030'

    // Ensure sticker design is written to board first
    writeStickerDesignToBoard({ boardRef: mainBoardRef, sticker: sticker })

    const newDraftSlap: Slap = {
      id: slapUseId,
      time: currTs,
      size: size,
      place: place.id,
      player: definitelyAnAddress,
      sticker: sticker.id,
      isDraft: true,
    }
    const newDraftFullSlap: ExpandedSlap = {
      id: slapUseId,
      time: currTs,
      size: size,
      place: place,
      sticker: sticker,
      player: definitelyAnAddress,
      isDraft: true,
    }
    await writeSlapToBoard({ boardRef: mainBoardRef, slap: newDraftSlap })
    setLatestSlap(newDraftSlap)
    setDraftSlaps([newDraftFullSlap]);
    setLatestUpdateTime(Date.now())
  };

  const handleSlapAction = async () => {
    if (hasPendingSlaps) {
      handleSlapExecutorOpen()
      return
    }
    if (!focusedPlace.placeId || !currentStickerId) {
      return
    }
    const place = getPlace({ boardRef: mainBoardRef, placeId: focusedPlace.placeId })
    const stickerDesign = getStickerDesign({ boardRef: mainBoardRef, stickerId: currentStickerId })
    if (!place || !stickerDesign) {
      return
    }
    await createDraftSlap(place, currentSlapSize, stickerDesign)
  }

  const handleCancelDraftSlap = () => {
    if (!draftSlaps || draftSlaps.length === 0) {
      return
    }
    setSlapPreCheckWarnings([]);
    deleteDraftSlap(draftSlaps[0].id)
  }

  const handleSlapCreatorOpen = () => {
    void handleSlapAction()
  }

  const getLockedViewForSlap = useCallback(
    (placeId: number | null, size: number): MapViewTarget | null => {
      if (!placeId) {
        return null
      }
      const sizedBounds = BlockPlaces.sizedLngLatBoundsFromPlaceId(placeId, size)
      const rawBounds = sizedBounds ?? BlockPlaces.lngLatBoundsFromPlaceId(placeId)
      // Pad bounds so the draft slap view isn't overly tight.
      const padFactor = size <= 1 ? 0.5 : 0.7;
      const bounds = rawBounds
        ? new LngLatBounds(
            [rawBounds.getWest() - (rawBounds.getEast() - rawBounds.getWest()) * padFactor,
             rawBounds.getSouth() - (rawBounds.getNorth() - rawBounds.getSouth()) * padFactor],
            [rawBounds.getEast() + (rawBounds.getEast() - rawBounds.getWest()) * padFactor,
             rawBounds.getNorth() + (rawBounds.getNorth() - rawBounds.getSouth()) * padFactor]
          )
        : null
      if (!bounds) {
        return null
      }
      const center = bounds.getCenter()
      return {
        center: [center.lng, center.lat],
        zoom: 14,
        bounds,
      }
    },
    []
  )

  useEffect(() => {
    const currentDraft = draftSlaps?.[0] ?? null
    const previousDraftId = lastDraftIdRef.current

    if (currentDraft && previousDraftId !== currentDraft.id) {
      const lockedView = getLockedViewForSlap(currentDraft.place.id, currentDraft.size || 1)
      if (lockedView) {
        draftCenterRef.current = lockedView.center
        setLockedMapView(lockedView)
        setMapLocked(true)
      }
      lastDraftIdRef.current = currentDraft.id
    } else if (!currentDraft && previousDraftId !== null) {
      const fallbackCenter =
        draftCenterRef.current ||
        (currDisplayedCenter.center
          ? [currDisplayedCenter.center.lng, currDisplayedCenter.center.lat]
          : null)
      if (fallbackCenter) {
        setLockedMapView({ center: fallbackCenter, zoom: 12 })
      } else {
        setLockedMapView(null)
      }
      setMapLocked(false)
      lastDraftIdRef.current = null
    }
  }, [draftSlaps, currDisplayedCenter.center, getLockedViewForSlap])

  const handleDeleteDraftSlap = (slapId: SlapId) => {
    setDeleteSlapOpenTimestamp(getCurrentTimestamp())
    setDeleteSlapId(slapId)
  }


  const handleDidSubmitTransaction = () => {
    // Only mark submitted on tx confirmation; actual clearing waits for sync
    markAllDraftSlapsAsSubmitted();
    setLatestUpdateTime(Date.now());
  }

  const markAllDraftSlapsAsSubmitted = () => {
    console.log('marking all draft slaps as submitted')
    setDraftSlaps(prevState => {
      if (prevState) {
        prevState.forEach(fullSlap => {
          console.log(`marking slap as submiited ${fullSlap.id}`)
          setStoredSlapSubmitted({ boardRef: mainBoardRef, slapId: fullSlap.id });
        });
        return prevState.map(fullSlap => ({ ...fullSlap, isSubmitted: true }));
      }
      return []
    })
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearFocusedPlace();
        startSlapping();
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [clearFocusedPlace, startSlapping])

  const mapDisplayClass = currMode === SlapMode ? '' : 'hidden'
  const createDisplayClass = currMode === CreateMode ? '' : 'hidden'
  const addTokenDisplayClass = currMode === AddTokenMode ? '' : 'hidden'
  const stickerBrowserDisplayClass = currMode === StickerBrowserMode ? '' : 'hidden'

  const startStickerBrowsing = () => {
    if (typeof window !== 'undefined') {
      stickerBrowserScrollRef.current = window.scrollY
    }
    setCurrMode(StickerBrowserMode)
  }

  const handleCornerBadgeClick = () => {
    if (typeof window !== 'undefined') {
      stickerBrowserScrollRef.current = window.scrollY
    }
    setCurrMode(StickerBrowserMode)
  }

  // Add URL parameter handling on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const slapParam = params.get('slap');
      const stickerParam = params.get('sticker');
      const playerParam = params.get('player');
      const placeParam = params.get('place');
      const source = params.get('source');

      if (slapParam) {
        const slapId = parseInt(slapParam, 10);
        // Only open the modal if coming from direct navigation
        if (source !== 'slapView') {
          setPreviewSlapId(slapId);
        }
        // Load the slap and move to its location
        getOrLoadSlap({ boardRef: mainBoardRef, slapId })
          .then(slap => {
            if (slap?.place) {
              // Use a small delay to ensure the map is ready
              setTimeout(() => {
                handleMoveMapToPlace(slap.place);
              }, 100);
            }
          });
      } else if (stickerParam) {
        const stickerId = parseInt(stickerParam, 10);
        // Show orientation modal if coming from sticker view
        if (source === 'stickerView') {
          // Load sticker to get its name
          getOrLoadStickerDesign({ boardRef: mainBoardRef, stickerId })
            .then(sticker => {
              if (sticker) {
                setOrientationStickerName(sticker.name);
              }
              setShowOrientationModal(true);
            });
        } else {
          // Show sticker preview modal for direct navigation
          setPreviewStickerId(stickerId);
        }
      } else if (playerParam) {
        setPreviewPlayerAddress(playerParam as Address);
      } else if (placeParam) {
        const placeId = parseInt(placeParam, 10);
        if (!Number.isNaN(placeId)) {
          setPendingUrlPlaceId(placeId);
        }
      }
      if (params.toString()) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    if (!mapReady || pendingUrlPlaceId === null) {
      return;
    }
    setFocusedPlace({ placeId: pendingUrlPlaceId, source: 'url' });
    setActiveSidebarMode(SidebarMode.Place);
    setPendingUrlPlaceId(null);
  }, [mapReady, pendingUrlPlaceId]);

  // Add a function to set sidebar mode and focused objective
  const setSidebarToObjectives = (objectiveId: number | null) => {
    setActiveSidebarMode(SidebarMode.Objectives);
    setFocusedObjectiveId(objectiveId);
  };

  const openPayoutReviewer = () => {
    setPayoutReviewerOpen(true);
  };

  const closePayoutReviewer = () => {
    setPayoutReviewerOpen(false);
  };

  const handleLocationFound = (lat: number, lng: number) => {
    console.log(`handleLocationFound ${lat}, ${lng}`)
    const placeId = BlockPlaces.enclosingPlaceIdForPoint(new LngLat(lng, lat))
    console.log(`placeId ${placeId}`)

    if (focusedPlace?.placeId === placeId) {
      const unionSquarePlaceId = 7147618599
      setFocusedPlace({ placeId: unionSquarePlaceId })
      return
    }

    setFocusedPlace({ placeId: placeId })
  };

  const handleClearActivities = useCallback(() => {
    setActivities([]);
  }, []);

  const handleStickerPublished = (
    stickerData: PublishedStickerData & { metadataCID?: string },
    shouldReview: boolean
  ) => {
    console.log('Handling published sticker:', stickerData);
    if (!shouldReview) {
      skipStickerReviewRef.current.add(stickerData.stickerId);
    }
    // Add to indexing set immediately when we get a new sticker
    setIndexingStickers(prev =>
      new Set([...Array.from(prev), stickerData.stickerId])
    );

    // Track this sticker for resource sync via server check
    setResourceTrackedStickers(prev => {
      const next = new Set(Array.from(prev));
      next.add(stickerData.stickerId);
      return next;
    });

    setRecentlyPublishedStickers(prev => {
      const filtered = prev.filter(s => s.stickerId !== stickerData.stickerId);
      return [stickerData, ...filtered].slice(0, 5);
    });
    // Create publish activity
    addPublishActivity(
      stickerData.stickerId,
      stickerData.name,
      stickerData.imageCID,
      stickerData.metadataCID
    );
    if (shouldReview) {
      startSlapping();
    }
    // Don't open reviewer immediately; wait for resource sync signal
  };

  // Handle when a sticker resource sync completes successfully
  const handleStickerSyncComplete = useCallback(
    (stickerId: StickerDesignId) => {
      console.log('Sticker sync completed for sticker', stickerId);

      const waitForLoaded = async () => {
        const start = Date.now();
        const timeoutMs = 30000; // 30s
        const intervalMs = 2000; // 2s

        while (Date.now() - start < timeoutMs) {
          const loaded = await getOrLoadStickerDesign({
            boardRef: mainBoardRef,
            stickerId
          });
          if (loaded) {
            return true;
          }
          await new Promise(r => setTimeout(r, intervalMs));
        }
        return false;
      };

      waitForLoaded().then((isLoaded) => {
        if (isLoaded) {
          if (!skipStickerReviewRef.current.has(stickerId)) {
            handleReviewStickerId(stickerId);
          } else {
            skipStickerReviewRef.current.delete(stickerId);
          }
        } else {
          console.log(
            'Could not load sticker after waiting; skipping open:',
            stickerId
          );
        }
        // Cleanup tracking in either case
        setIndexingStickers(prev => {
          const next = new Set(Array.from(prev));
          next.delete(stickerId);
          return next;
        });
        setResourceTrackedStickers(prev => {
          const next = new Set(Array.from(prev));
          next.delete(stickerId);
          return next;
        });
      });
    },
    [mainBoardRef, handleReviewStickerId]
  );

  const handleStickerSyncError = useCallback(
    (stickerId: StickerDesignId) => {
      console.log(`Sticker sync error for sticker ${stickerId}`);
      setIndexingStickers(prev => {
        const next = new Set(Array.from(prev));
        next.delete(stickerId);
        return next;
      });
      setResourceTrackedStickers(prev => {
        const next = new Set(Array.from(prev));
        next.delete(stickerId);
        return next;
      });
    },
    []
  );

  return (
    <div className='flex w-full'>
      {/* Hidden element for screenshot automation */}
      {mapReady && (
        <div
          id="featured_content_loaded"
          style={{ display: 'none' }}
          aria-hidden="true"
        />
      )}
      <div className={`${mapDisplayClass}  w-full`}>
        <div
          className="flex flex-col md:flex-row w-full justify-start md:justify-evenly"
        >
          {/* Mobile GameTitle + Account */}
          <div className="block md:hidden w-full mt-3">
            <div className="flex items-start justify-between px-4 gap-3">
              <div className="flex-1 min-w-0 pr-2">
                <GameTitleMobile logoSrc={logoSrc} />
              </div>
              <div className="flex shrink-0 justify-end">
                {playerAddress ? (
                  <button
                    onClick={() => handleReviewPlayer(playerAddress)}
                    className="max-w-[180px] rounded-full bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition whitespace-nowrap truncate"
                  >
                    {player?.ens || formatEthAddress(playerAddress, 4)}
                  </button>
                ) : (
                  <ConnectAccount
                    variant="compact"
                    buttonClassName="rounded-full bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition whitespace-nowrap"
                  />
                )}
              </div>
            </div>
          </div>

          {!shouldHideActivity && (
            <div className="fixed top-4 left-4 z-50">
              <RecentActivity
                activities={activities}
                onSlapClick={handleReviewSlapId}
                onStickerClick={handleReviewStickerId}
                indexingSlaps={indexingSlaps}
                indexingStickers={indexingStickers}
                setFocusOnPlace={setFocusOnPlace}
                boardRef={mainBoardRef}
                onClearActivities={handleClearActivities}
                getSlapIndexingStatus={getSlapIndexingStatus}
              />
            </div>
          )}

          {/* Resource locator tracking for user-created slaps */}
          <MultiSlapResourceTracker
            slapIds={resourceTrackedSlaps}
            onSlapComplete={(slapId, result) => handleResourceComplete(slapId)(result)}
            onSlapError={(slapId, result) => handleResourceError(slapId)(result)}
          />

          {/* Resource tracking for newly published stickers */}
          <MultiStickerResourceTracker
            stickerIds={Array.from(resourceTrackedStickers)}
            onStickerComplete={(stickerId) =>
              handleStickerSyncComplete(stickerId)
            }
            onStickerError={(stickerId) =>
              handleStickerSyncError(stickerId)
            }
          />

          <div className="md:hidden px-4">
            <SlapperButtonSection
              sticker={currentSticker}
              hasPendingSlap={hasPendingSlaps}
              hasConnectedWallet={!!playerAddress}
              currentSlapSize={currentSlapSize}
              onSlapSizeChange={handleSlapSizeWasChanged}
              onPickStickers={handleCornerBadgeClick}
              onSlapAction={handleSlapAction}
              onCancelDraft={handleCancelDraftSlap}
              isSlapDisabled={!currentStickerId || !focusedPlace.placeId}
              pendingSlaps={draftSlaps}
              instructionText={gameDescription}
              preCheckWarningsContent={
                hasPendingSlaps && slapPreCheckWarnings.length > 0 ? (
                  <SlapCheckWarnings
                    boardRef={mainBoardRef}
                    warnings={slapPreCheckWarnings}
                    pendingSlaps={draftSlaps}
                    objectives={getObjectives({ boardRef: mainBoardRef })}
                  />
                ) : null
              }
            />
          </div>

          <div className="relative w-full mt-3 md:mt-0 md:flex-1 md:w-auto">
            <LocationBadge onLocationFound={handleLocationFound} />
            <SlapMap
              boardRef={mainBoardRef}
              latestSlap={latestSlap}
              realtimeLatestSlap={realtimeLatestSlap}
              clientDisplayedCenter={currDisplayedCenter}
              setClientDisplayedCenter={setCurrDisplayedCenter}
              setFocusOnPlace={setFocusOnPlace}
              focusedPlace={focusedPlace}
              handleSlapExecutorOpen={handleSlapExecutorOpen}
              handleSlapCreatorOpen={handleSlapCreatorOpen}
              slapsInProgress={draftSlaps}
              deletedSlaps={deletedSlaps}
              latestUpdateTime={latestUpdateTime}
              onMapReady={() => setMapReady(true)}
              initialPlaceId={initialPlaceId}
              lockedView={lockedMapView}
              isLocked={mapLocked}
            />
          </div>

            <div
              className="flex flex-wrap flex-col items-center px-4 md:px-2 mt-6 w-full md:w-[360px] lg:w-[400px] md:flex-none"
            >
            {/* Desktop GameTitle + Account */}
            <div className="hidden md:flex w-full items-start justify-between gap-4 px-1">
              <GameTitle logoSrc={logoSrc} />
              {playerAddress ? (
                <button
                  onClick={() => handleReviewPlayer(playerAddress)}
                  className="mt-1 rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition whitespace-nowrap"
                >
                  {player?.ens || formatEthAddress(playerAddress, 5)}
                </button>
              ) : (
                <ConnectAccount
                  variant="compact"
                  buttonClassName="mt-1 rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition whitespace-nowrap"
                />
              )}
            </div>
            <div className="hidden md:block">
              <SlapperButtonSection
              sticker={currentSticker}
              hasPendingSlap={hasPendingSlaps}
              hasConnectedWallet={!!playerAddress}
              currentSlapSize={currentSlapSize}
              onSlapSizeChange={handleSlapSizeWasChanged}
              onPickStickers={handleCornerBadgeClick}
                onSlapAction={handleSlapAction}
                onCancelDraft={handleCancelDraftSlap}
                isSlapDisabled={!currentStickerId || !focusedPlace.placeId}
                pendingSlaps={draftSlaps}
                instructionText={gameDescription}
                preCheckWarningsContent={
                  hasPendingSlaps && slapPreCheckWarnings.length > 0 ? (
                    <SlapCheckWarnings
                      boardRef={mainBoardRef}
                      warnings={slapPreCheckWarnings}
                      pendingSlaps={draftSlaps}
                      objectives={getObjectives({ boardRef: mainBoardRef })}
                    />
                  ) : null
                }
              />
            </div>

            {/* Slap Here button - duplicate of map functionality */}
            {/* Temporarily hidden - using sidebar button instead
            {focusedPlace.placeId && (
              <div className='mt-4'>
                <button
                  onClick={handleSlapCreatorOpen}
                  className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors shadow-md"
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                  }}
                >
                  Create Slap
                </button>
              </div>
            )}
            */}

            <SlapExecutooor pendingSlaps={draftSlaps}
              boardRef={mainBoardRef}
              handleDeletePendingSlap={handleDeleteDraftSlap}
              handleDidSubmitTransaction={handleDidSubmitTransaction}
              setOpenTimestamp={slapExecutorOpenTimestamp}
              addSlapActivity={addSlapActivity}
              renderTriggerButton={false}
              autoExecuteOnOpen
              suppressDialogOpen
              onDialogOpenChange={setIsSlapDialogOpen}
              onShareOverlayChange={setIsShareOverlayOpen}
              onPreCheckWarningsChange={setSlapPreCheckWarnings} />

            <GameSidebar
              boardRef={mainBoardRef}
              focusedPlace={focusedPlace}
              setFocusOnPlace={setFocusedPlace}
              activeSidebarMode={activeSidebarMode}
              setActiveSidebarMode={setActiveSidebarMode}
              handleReviewStickerId={handleReviewStickerId}
              handleReviewSlapId={handleReviewSlapId}
              focusedObjectiveId={focusedObjectiveId}
              setFocusedObjectiveId={setFocusedObjectiveId}
              handleSlapCreatorOpen={handleSlapCreatorOpen}
            />

            <DeleteSlapDialog
              boardRef={mainBoardRef}
              slapToDelete={deleteSlapId}
              deletePendingSlap={deleteDraftSlap}
              setOpenTimestamp={deleteSlapDialogOpenTimestamp}
            />

            <div className="px-4 mt-4 text-xs text-slate-600 leading-snug dark:text-slate-300">
              Publish a sticker and get paid when anyone slaps it:
            </div>

            <div className='mt-4 mb-6 flex flex-col gap-2'>
              <button
                onClick={(e) => { handleStartPublishing() }}
                className="border border-gray-400 bg-gray-200 rounded-md"
              >
                <div className='text-slate-600 font-semibold py-2 px-4 rounded'>
                  Publish a Sticker
                </div>
              </button>
            </div>

            <div className='px-4 mt-1'>
              <Link
                href="/doc/about"
                className="text-blue-600 p-2 text-md hover:text-blue-800 underline"
              >
                About
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className={`${createDisplayClass} w-full`}>
        <div className="flex min-h-screen flex-col items-center justify-start">
          <StickerCreator
            boardRef={mainBoardRef}
            handleDidClose={startSlapping}
            handleStartAddingToken={startAddingToken}
            realtimeLatestStickerDesign={realtimeLatestStickerDesign}
            onStickerPublished={handleStickerPublished}
          />
        </div>
      </div>
      <div className={`${addTokenDisplayClass} w-full`}>
        <div className="flex min-h-screen flex-col items-center justify-start">
          <AddNewToken handleDidClose={startCreating} />
        </div>
      </div>
      <div className={`${stickerBrowserDisplayClass} w-full`}>
        <div className="flex min-h-screen flex-col items-center justify-start">
          <StickerBrowser
            handleDidClose={startSlapping}
            boardRef={mainBoardRef}
            handleStartPublishing={handleStartPublishing}
            previewStickerId={previewStickerId}
            setPreviewStickerId={setPreviewStickerId}
          />
        </div>
      </div>
      <StickerReviewer
        stickerId={previewStickerId}
        boardRef={mainBoardRef}
        onClose={closeStickerReviewer}
        handleStickerChoice={handleStickerChoice}
        handleReviewPlayer={handleReviewPlayer}
      />
      <SlapReviewer
        slapId={previewSlapId}
        boardRef={mainBoardRef}
        onClose={closeSlapReviewer}
        handleReviewPlace={handleMoveMapToPlace}
        handleReviewPlaceOnly={handleMoveMapToPlace}
        handleReviewSticker={handleReviewSticker}
        handleReviewPlayer={handleReviewPlayer}
        setActiveSidebarMode={setActiveSidebarMode}
        setSidebarToObjectives={setSidebarToObjectives}
      />
      <PlayerReviewer
        playerAddress={previewPlayerAddress}
        boardRef={mainBoardRef}
        onClose={closePlayerReviewer}
        handleReviewPlace={handleMoveMapToPlace}
        handleReviewSticker={handleReviewSticker}
        handleReviewSlap={handleReviewSlapId}
        startPayoutView={openPayoutReviewer}
        startPublishingView={handleStartPublishing}
      />
      {showOrientationModal && (
        <SlapOrientationModal 
          onClose={() => setShowOrientationModal(false)}
          stickerName={orientationStickerName}
        />
      )}
      <PayoutReviewer
        isOpen={payoutReviewerOpen}
        boardRef={mainBoardRef}
        onClose={closePayoutReviewer}
      />
    </div>

  )
}
